import React from 'react'
import TableHeader from '../../components/table/TableHeader'
import TableLayout from '../../components/table/TableLayout'
import TableItem from '../../components/table/TableItem'
import Text from '../../components/text/Text'
import WizButton from '../../components/button/WizButton'
import WizSwitchButton from '../../components/button/WizSwitchButton'
import ContentBlocksInlinePreview from './ContentBlocksInlinePreview'
import {AI_FEED_CATEGORY_LABEL, AI_FEED_GROUPS} from './constants'
import {IFeedItem, PostType} from './types'

interface IProps {
    className?: string
    postType: PostType
    items: IFeedItem[]
    onEdit: (item: IFeedItem) => void
    onDelete: (item: IFeedItem) => void
    onToggleActive: (item: IFeedItem) => void
}

const GRID_COLS = 'grid-cols-[1fr_160px_120px_100px]'

const HEADER_CLASS = '!rounded-b-none py-[13px]'
const HEADER_TEXT_CLASS = 'text-font01 text-ti3 font-bold uppercase tracking-wide'

const FeedList: React.FC<IProps> = ({className, postType, items, onEdit, onDelete, onToggleActive}) => {
    const renderRow = (item: IFeedItem) => (
        // 행 클릭 → 문구 수정(PUT). 토글/삭제 버튼은 stopPropagation으로 편집 진입을 막는다.
        <TableItem
            key={item?.id}
            className={`${GRID_COLS} space-x-[30px] ${item?.active === false ? 'opacity-50' : ''}`}
            onClick={() => onEdit(item)}>
            <Text className={'text-body2 truncate'}>
                <ContentBlocksInlinePreview
                    blocks={item?.content_blocks}
                    imageClassName={'block w-[600px] h-auto mx-auto my-[10px] rounded-[6px]'}
                />
            </Text>
            <Text className={'text-center text-ti3 text-font03'}>
                {item?.category ? AI_FEED_CATEGORY_LABEL[item.category] : '-'}
            </Text>
            <div className={'flex justify-center'}>
                <WizSwitchButton
                    state={item?.active}
                    onClick={(e: React.MouseEvent) => {
                        e.stopPropagation()
                        onToggleActive(item)
                    }}>
                    {item?.active ? '활성' : '비활성'}
                </WizSwitchButton>
            </div>
            <div className={'flex justify-center'}>
                <WizButton
                    size={'small'}
                    type={'red'}
                    text={'삭제'}
                    onClick={e => {
                        e.stopPropagation()
                        onDelete(item)
                    }}
                />
            </div>
        </TableItem>
    )

    const renderHeader = () => (
        <TableHeader className={`${GRID_COLS} space-x-[30px] md:hidden ${HEADER_CLASS}`}>
            <Text className={HEADER_TEXT_CLASS}>Content</Text>
            <Text className={`${HEADER_TEXT_CLASS} text-center`}>Category</Text>
            <Text className={`${HEADER_TEXT_CLASS} text-center`}>노출</Text>
            <Text className={`${HEADER_TEXT_CLASS} text-center`}>삭제</Text>
        </TableHeader>
    )

    if (postType !== 'auto_generated') {
        return (
            <div className={`${className}`}>
                {renderHeader()}
                <TableLayout className={'!rounded-t-none !mt-0'}>
                    {items?.map(renderRow)}
                    {!items?.length && (
                        <TableItem className={'text-center'}>
                            <div>
                                <Text>No Items</Text>
                            </div>
                        </TableItem>
                    )}
                </TableLayout>
            </div>
        )
    }

    const groups = AI_FEED_GROUPS.map(group => ({
        ...group,
        rows: items.filter(item => item.category && group.categories.includes(item.category)),
    })).filter(group => group.rows.length)

    return (
        <div className={`space-y-[32px] ${className}`}>
            {groups.map(group => (
                <div key={group.key}>
                    <div className={'flex items-center gap-x-[8px] mb-[10px]'}>
                        <Text className={'text-h3 font-bold text-font01'}>{group.label}</Text>
                        <Text className={'text-ti3 text-font03'}>{group.rows.length}건</Text>
                    </div>
                    {renderHeader()}
                    <TableLayout className={'!rounded-t-none !mt-0'}>{group.rows.map(renderRow)}</TableLayout>
                </div>
            ))}

            {!groups.length && (
                <>
                    {renderHeader()}
                    <TableLayout className={'!rounded-t-none !mt-0'}>
                        <TableItem className={'text-center'}>
                            <div>
                                <Text>No Items</Text>
                            </div>
                        </TableItem>
                    </TableLayout>
                </>
            )}
        </div>
    )
}

export default FeedList
